// Shared rendering & theme utilities
import React from 'react';
import 'katex/dist/katex.min.css';
import { InlineMath, BlockMath } from 'react-katex';

/** Render text with inline $...$ and block $$...$$ LaTeX */
export function MathText({ text, className = '' }) {
  if (!text) return null;
  const blocks = String(text).split(/(\$\$[\s\S]+?\$\$)/g);
  return (
    <span className={className}>
      {blocks.map((block, blockIdx) => {
        const blockKey = `b-${blockIdx}-${block.length}-${block.slice(0, 8)}`;
        if (block.startsWith('$$') && block.endsWith('$$')) {
          try { return <span key={blockKey} className="block my-3"><BlockMath math={block.slice(2, -2)} /></span>; }
          catch { return <span key={blockKey}>{block}</span>; }
        }
        const parts = block.split(/(\$[^$\n]+\$)/g);
        return (
          <React.Fragment key={blockKey}>
            {parts.map((p, pi) => {
              const partKey = `p-${blockIdx}-${pi}-${p.slice(0, 8)}`;
              if (p.startsWith('$') && p.endsWith('$') && p.length > 2) {
                try { return <InlineMath key={partKey} math={p.slice(1, -1)} />; }
                catch { return <span key={partKey}>{p}</span>; }
              }
              return <span key={partKey} style={{ whiteSpace: 'pre-wrap' }}>{p}</span>;
            })}
          </React.Fragment>
        );
      })}
    </span>
  );
}

export const ACCENT = {
  indigo: { text: 'text-indigo-300', glow: 'shadow-[0_0_24px_-8px_rgba(129,140,248,0.5)]', dot: 'bg-indigo-400', soft: 'bg-indigo-500/10', ring: 'ring-indigo-400/30', grad: 'from-indigo-400/20 to-violet-400/10' },
  rose:    { text: 'text-rose-300',   glow: 'shadow-[0_0_24px_-8px_rgba(251,113,133,0.5)]',  dot: 'bg-rose-400',   soft: 'bg-rose-500/10',   ring: 'ring-rose-400/30',   grad: 'from-rose-400/20 to-pink-400/10' },
  amber:   { text: 'text-amber-300',  glow: 'shadow-[0_0_24px_-8px_rgba(251,191,36,0.5)]',   dot: 'bg-amber-400',  soft: 'bg-amber-500/10',  ring: 'ring-amber-400/30',  grad: 'from-amber-400/20 to-orange-400/10' },
  emerald: { text: 'text-emerald-300',glow: 'shadow-[0_0_24px_-8px_rgba(52,211,153,0.5)]',   dot: 'bg-emerald-400',soft: 'bg-emerald-500/10',ring: 'ring-emerald-400/30',grad: 'from-emerald-400/20 to-teal-400/10' },
  sky:     { text: 'text-sky-300',    glow: 'shadow-[0_0_24px_-8px_rgba(56,189,248,0.5)]',   dot: 'bg-sky-400',    soft: 'bg-sky-500/10',    ring: 'ring-sky-400/30',    grad: 'from-sky-400/20 to-blue-400/10' },
  slate:   { text: 'text-slate-300',  glow: 'shadow-[0_0_24px_-8px_rgba(148,163,184,0.4)]',  dot: 'bg-slate-400',  soft: 'bg-slate-500/10',  ring: 'ring-slate-400/20',  grad: 'from-slate-400/15 to-zinc-400/10' },
};
export const accent = (name) => ACCENT[name] || ACCENT.indigo;

export function difficultyTone(d) {
  switch ((d || '').toLowerCase()) {
    case 'easy':      return 'text-emerald-300 bg-emerald-400/10 ring-emerald-400/20';
    case 'medium':    return 'text-amber-300 bg-amber-400/10 ring-amber-400/20';
    case 'hard':      return 'text-rose-300 bg-rose-400/10 ring-rose-400/20';
    case 'very hard': return 'text-red-300 bg-red-400/10 ring-red-400/20';
    default:          return 'text-slate-300 bg-slate-400/10 ring-slate-400/20';
  }
}

export const springs = {
  soft: { type: 'spring', stiffness: 260, damping: 28, mass: 0.7 },
  snappy: { type: 'spring', stiffness: 420, damping: 32, mass: 0.6 },
  slow: { type: 'spring', stiffness: 180, damping: 26, mass: 0.9 },
};
